import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Shield, Users, CheckCircle, Building, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCookieConsent } from '../../contexts/CookieConsentContext';

const StaffLoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const { openPreferences } = useCookieConsent();
  const { staffLogin } = useAuth();
  const navigate = useNavigate();

  // Prevent search engine indexing - Staff portal should NOT be public
  useEffect(() => {
    // Add noindex meta tag
    const metaNoIndex = document.createElement('meta');
    metaNoIndex.name = 'robots';
    metaNoIndex.content = 'noindex, nofollow';
    document.head.appendChild(metaNoIndex);

    // Update page title
    document.title = 'Staff Portal Access - Receevit®';

    // Cleanup on unmount
    return () => {
      document.head.removeChild(metaNoIndex);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

    const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await staffLogin(formData.email, formData.password);
      
      if (result && result.success) {
        // Navigate to staff portal after successful login
        navigate('/staff');
      } else {
        setError(result?.error || 'Staff login failed');
      }
    } catch (error) {
      console.error('Staff login error:', error);
      setError('Staff login failed');
    }

    setIsLoading(false);
  };

  const features = [
    {
      icon: Shield,
      title: 'Secure Staff Access',
      description: 'Enterprise-grade security for your internal team'
    },
    {
      icon: Users,
      title: 'Client Management',
      description: 'Manage all client accounts and relationships'
    },
    {
      icon: Building,
      title: 'System Administration',
      description: 'Full control over platform settings and operations'
    }
  ];

  return (
    <div className="min-h-screen bg-[#1F11ED]">

      {/* Staff Portal Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">Staff Portal</span>
            </div>
            <Link
              to="/"
              className="text-white/80 hover:text-white transition-colors duration-200"
            >
              ← Back to Main Site
            </Link>
          </div>
        </div>
      </div>

      <div className="pt-8">
        {/* Hero Section */}
        <section className="relative py-12">

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-6xl font-bold text-white mb-6"
            >
              Staff Portal{' '}
              <span className="bg-gradient-to-r from-purple-300 to-white bg-clip-text text-transparent">
                Access
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-blue-100 mb-4 max-w-3xl mx-auto"
            >
              Secure access to manage clients, invoices, and system operations
            </motion.p>
          </div>
        </section>

        {/* Login Form Section */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-md mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl p-6 border border-white/20"
              >
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <Lock className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">Staff Authentication</h2>
                  <p className="text-blue-200">
                    Access restricted to authorized staff members only
                  </p>
                  
                  {/* Admin/Staff Only Notice */}
                  <div className="mt-3 p-2 bg-blue-600/30 border border-blue-400/30 rounded-lg">
                    <p className="text-xs text-blue-100">
                      <strong>Admin & Staff Users Only</strong> - Regular clients should use the{' '}
                      <Link to="/login" className="text-white hover:text-blue-200 underline">
                        Client Login
                      </Link>{' '}
                      instead.
                    </p>
                  </div>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-500/20 border border-red-400/30 rounded-xl text-red-200">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-blue-200 mb-2">
                      Staff Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200"
                      placeholder="staff@company.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-blue-200 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 pr-12 bg-white/20 border border-white/30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200"
                        placeholder="Enter your password"
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(prev => !prev)}
                        className="absolute inset-y-0 right-3 flex items-center text-white/80 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded-full transition-opacity"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Error Display */}
                  {error && (
                    <div className="mb-4 p-3 bg-red-500/20 border border-red-400/30 rounded-lg">
                      <p className="text-red-200 text-sm">{error}</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="remember"
                        name="rememberMe"
                        checked={formData.rememberMe}
                        onChange={handleChange}
                        className="w-4 h-4 text-blue-600 bg-white/20 border-white/30 rounded focus:ring-blue-400 focus:ring-2"
                      />
                      <label htmlFor="remember" className="text-sm text-blue-200">
                        Remember me
                      </label>
                    </div>
                    <Link
                      to="/staff/forgot-password"
                      className="text-sm text-blue-300 hover:text-white transition-colors duration-200"
                    >
                      Forgot password?
                    </Link>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-xl font-semibold hover:bg-blue-700 hover:shadow-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isLoading ? 'Authenticating...' : 'Access Staff Portal'}
                  </button>
                  

                </form>

                <div className="mt-4 text-center">
                  <p className="text-blue-200 text-sm">
                    Need help? Contact your system administrator
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Staff Portal Features</h2>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Comprehensive tools for managing your business operations
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 text-center border border-white/20 hover:bg-white/15 transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-blue-200 text-sm">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Security Notice */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
                <h2 className="text-3xl font-bold text-white mb-4">
                  Secure & Compliant
                </h2>
                <p className="text-xl text-blue-100 mb-8">
                  This portal is protected by enterprise-grade security measures and complies with industry standards
                </p>
                <div className="flex items-center justify-center space-x-6 text-blue-200">
                  <span className="flex items-center">
                    <Shield className="w-5 h-5 mr-2" />
                    SSL Encrypted
                  </span>
                  <span className="flex items-center">
                    <Lock className="w-5 h-5 mr-2" />
                    Role-Based Access
                  </span>
                  <span className="flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Audit Logged
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-white/10 backdrop-blur-lg border-t border-white/20 mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Company Info */}
              <div className="col-span-1 md:col-span-2">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-xl font-bold text-white">Receevit® Staff Portal</span>
                </div>
                <p className="text-blue-200 text-sm mb-4">
                  Secure, enterprise-grade staff management portal for managing clients, invoices, and system operations.
                </p>
                <div className="flex space-x-4">
                  <a href="#" className="text-blue-200 hover:text-white transition-colors duration-200">
                    <Shield className="w-5 h-5" />
                  </a>
                  <a href="#" className="text-blue-200 hover:text-white transition-colors duration-200">
                    <Lock className="w-5 h-5" />
                  </a>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="text-white font-semibold mb-4">Quick Links</h3>
                <ul className="space-y-2">
                  <li>
                    <a href="/" className="text-blue-200 hover:text-white transition-colors duration-200 text-sm">
                      Main Website
                    </a>
                  </li>
                  <li>
                    <a href="/support" className="text-blue-200 hover:text-white transition-colors duration-200 text-sm">
                      Support
                    </a>
                  </li>
                  <li>
                    <a href="/about" className="text-blue-200 hover:text-white transition-colors duration-200 text-sm">
                      About Us
                    </a>
                  </li>
                </ul>
              </div>

              {/* Contact */}
              <div>
                <h3 className="text-white font-semibold mb-4">Contact</h3>
                <ul className="space-y-2">
                  <li className="text-blue-200 text-sm">
                    <span className="block">System Admin</span>
                    <span className="text-blue-300">admin@receevit.com</span>
                  </li>
                  <li className="text-blue-200 text-sm">
                    <span className="block">Technical Support</span>
                    <span className="text-blue-300">support@receevit.com</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-white/20 mt-8 pt-6 flex flex-col md:flex-row justify-between items-center">
              <div className="text-blue-200 text-sm">
                © {new Date().getFullYear()} Receevit®. All rights reserved.
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-4 md:mt-0">
                <Link to="/privacy" className="text-blue-200 hover:text-white transition-colors duration-200 text-sm">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="text-blue-200 hover:text-white transition-colors duration-200 text-sm">
                  Terms of Use
                </Link>
                <Link to="/cookies" className="text-blue-200 hover:text-white transition-colors duration-200 text-sm">
                  Cookie Policy
                </Link>
                <button
                  type="button"
                  onClick={openPreferences}
                  className="text-blue-200 hover:text-white transition-colors duration-200 text-sm"
                >
                  Cookie Preferences
                </button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default StaffLoginPage;
